import { useEffect, useState } from "react";
import { adminListChangelog, adminCreateChangelog, adminUpdateChangelog, adminDeleteChangelog, fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { DEFAULT_CHANGELOG_CATEGORIES } from "@/pages/Changelog";
import { Megaphone, Plus, Trash2, Eye, EyeOff, Loader2, ExternalLink, Pencil, FolderPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const today = () => new Date().toISOString().slice(0, 10);
const slugify = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function AdminChangelog() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", tag: "feature", date: today() });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const load = () => adminListChangelog().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    fetchAdminSettings().then((s) => {
      const c = Array.isArray(s.changelog_categories) && s.changelog_categories.length ? s.changelog_categories : DEFAULT_CHANGELOG_CATEGORIES;
      setCats(c.filter((x) => x && x.key));
      setForm((f) => ({ ...f, tag: c[0]?.key || "feature" }));
    }).catch(() => setCats(DEFAULT_CHANGELOG_CATEGORIES));
  }, []);

  const saveCats = async (next) => {
    setCats(next);
    try { await adminUpdateSettings({ changelog_categories: next }); toast("Sections saved"); }
    catch { toast.error("Could not save sections"); }
  };
  const addCat = () => {
    const en = window.prompt("New section name (e.g. Integrations)"); if (!en) return;
    const key = slugify(en); if (!key) return;
    if ((cats || []).find((c) => c.key === key)) { toast("That section already exists"); return; }
    saveCats([...(cats || []), { key, en: en.trim(), hi: en.trim() }]);
  };
  const renameCat = (key) => {
    const cur = (cats || []).find((c) => c.key === key);
    const en = window.prompt("Rename section (English label)", cur?.en || key); if (en == null) return;
    const hi = window.prompt("Hindi label (देवनागरी)", cur?.hi || en) ?? (cur?.hi || en);
    saveCats((cats || []).map((c) => c.key === key ? { ...c, en: en.trim() || key, hi: hi.trim() || en.trim() } : c));
  };
  const deleteCat = (key) => {
    if (!window.confirm("Remove this section? Existing entries tagged with it will move to 'Other' until retagged.")) return;
    saveCats((cats || []).filter((c) => c.key !== key));
  };

  const add = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      await adminCreateChangelog(form);
      setForm({ title: "", body: "", tag: (cats && cats[0]?.key) || "feature", date: today() });
      toast("What's New entry published");
      load();
    } catch (e) { toast.error("Could not save", { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const toggleHide = async (e) => { try { await adminUpdateChangelog(e.id, { hidden: !e.hidden }); load(); } catch { /* ignore */ } };
  const remove = async (id) => { if (!window.confirm("Delete this entry?")) return; try { await adminDeleteChangelog(id); load(); } catch { /* ignore */ } };
  const labelFor = (key) => (cats || []).find((c) => c.key === key)?.en || key;

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-changelog">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Megaphone className="h-6 w-6 text-saffron" />
          <h1 className="font-serif-latin text-3xl text-ink">What's New</h1>
        </div>
        <a href="/changelog" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-saffron hover:underline">View public page <ExternalLink className="h-3.5 w-3.5" /></a>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-ink-3">Publish updates. They appear on the public <span className="font-mono text-xs">/changelog</span> (“What's New”) page, grouped by section.</p>

      {/* Section manager */}
      <div className="mb-6 border border-line bg-surface p-5" data-testid="changelog-sections">
        <div className="mb-3 flex items-center justify-between">
          <p className="overline-label text-gold">Sections</p>
          <button data-testid="changelog-add-section" onClick={addCat} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron"><FolderPlus className="h-3.5 w-3.5" /> Add section</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(cats || []).map((c) => (
            <span key={c.key} data-testid={`changelog-section-chip-${c.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-parchment px-3 py-1 text-xs text-ink-2">
              {c.en}
              <button onClick={() => renameCat(c.key)} title="Rename" className="text-ink-3 hover:text-saffron"><Pencil className="h-3 w-3" /></button>
              <button onClick={() => deleteCat(c.key)} title="Remove" className="text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
          {cats && cats.length === 0 && <span className="text-xs text-ink-3">No sections yet — add one.</span>}
        </div>
      </div>

      {/* Composer */}
      <div className="mb-8 border border-line bg-surface p-5" data-testid="changelog-composer">
        <p className="mb-3 overline-label text-gold">New entry</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid="changelog-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (e.g. Muhurta Finder is live)" className={`${inp} sm:col-span-2`} />
          <select data-testid="changelog-tag" value={form.tag} onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))} className={inp}>
            {(cats || []).map((c) => <option key={c.key} value={c.key}>{c.en}</option>)}
          </select>
          <input data-testid="changelog-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inp} />
          <textarea data-testid="changelog-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={3} placeholder="Describe the change (optional)" className={`${inp} sm:col-span-2`} />
        </div>
        <button data-testid="changelog-save" onClick={add} disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish entry
        </button>
      </div>

      {/* List */}
      {items === null ? <p className="py-16 text-center text-ink-3">…</p> : items.length === 0 ? (
        <p className="py-16 text-center text-ink-3">No entries yet.</p>
      ) : (
        <>
          {(() => {
            const total = items.length;
            const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
            const cur = Math.min(page, pageCount);
            const start = (cur - 1) * PAGE_SIZE;
            const pageItems = items.slice(start, start + PAGE_SIZE);
            return (
              <>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm text-ink-3" data-testid="changelog-count">
                    {total} entries · showing {start + 1}–{Math.min(start + PAGE_SIZE, total)}
                  </p>
                  <span className="text-xs text-ink-3">Page {cur} / {pageCount}</span>
                </div>
                <ul className="space-y-3">
                  {pageItems.map((e) => (
                    <li key={e.id} data-testid={`changelog-item-${e.id}`} className={`flex items-start justify-between gap-4 border border-line bg-surface p-4 ${e.hidden ? "opacity-60" : ""}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-saffron/15 px-2 py-0.5 text-[11px] uppercase tracking-wide text-saffron">{labelFor(e.tag)}</span>
                          <span className="text-xs text-ink-3">{e.date}</span>
                          {e.hidden && <span className="text-xs text-amber-600">hidden</span>}
                        </div>
                        <p className="mt-1.5 font-serif-latin text-lg text-ink">{e.title}</p>
                        {e.body && <p className="mt-1 whitespace-pre-line text-sm text-ink-2">{e.body}</p>}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button onClick={() => toggleHide(e)} title={e.hidden ? "Show" : "Hide"} className="p-2 text-ink-3 hover:text-ink">{e.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                        <button onClick={() => remove(e.id)} title="Delete" className="p-2 text-ink-3 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </li>
                  ))}
                </ul>
                {pageCount > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-3" data-testid="changelog-pagination">
                    <button data-testid="changelog-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={cur <= 1}
                      className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-40 disabled:hover:border-line disabled:hover:text-ink-2">
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </button>
                    <span className="text-sm text-ink-3">{cur} / {pageCount}</span>
                    <button data-testid="changelog-next" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={cur >= pageCount}
                      className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-40 disabled:hover:border-line disabled:hover:text-ink-2">
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
